import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ArrowLeft, LogOut, Clock, UserX, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSessions = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (user) {
      // Only allow primary admin
      if (user.email.toLowerCase() !== 'rebelangelstheband@gmail.com') {
        toast.error('Access denied. Only primary admin can view active sessions.');
        navigate('/admin/dashboard');
        return;
      }
      fetchActiveSessions();
    }
  }, [user, loading, navigate]);

  const fetchActiveSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await axios.get(`${API}/admin/active-sessions`, { withCredentials: true });
      setSessions(response.data.active_sessions);
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Only primary admin can view sessions.');
        navigate('/admin/dashboard');
      } else {
        toast.error('Failed to load active sessions');
      }
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const forceLogout = async (sessionId, userName) => {
    const confirmMessage = `Force logout "${userName}"?\n\nThey will be immediately signed out of the admin panel.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/force-logout/${sessionId}`, { withCredentials: true });
      toast.success(`${userName} has been logged out`);
      fetchActiveSessions(); // Refresh the list
    } catch (error) {
      console.error('Error forcing logout:', error);
      if (error.response?.status === 400) {
        toast.error('Cannot logout your own session');
      } else {
        toast.error('Failed to force logout user');
      }
    }
  };

  const formatActualLoginTime = (dateString) => {
    // Parse the server UTC time correctly
    const loginTime = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
    
    // Format as local time with timezone info
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    };
    
    return loginTime.toLocaleString('en-US', options);
  };

  const cleanupExpiredSessions = async () => {
    try {
      const response = await axios.post(`${API}/admin/cleanup-sessions`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchActiveSessions(); // Refresh the list
    } catch (error) {
      console.error('Error cleaning up sessions:', error);
      toast.error('Failed to cleanup expired sessions');
    }
  };

  const formatExpiryTime = (dateString) => {
    // Parse the server UTC time correctly by ensuring Z suffix
    const expiry = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
    const now = new Date();
    
    const diffMs = expiry - now;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    
    if (diffMins <= 0) return 'Expired';
    if (diffMins < 60) return `${diffMins} minutes remaining`;
    if (diffHours < 3) return `${diffHours}h ${remainingMins}m remaining`;
    return `~${diffHours} hours remaining`;
  };

  if (loading || loadingSessions) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels" 
              className="admin-brand-logo"
            />
          </div>
          <div className="admin-user-section">
            <img src={user.picture} alt={user.name} className="admin-user-avatar" />
            <span className="admin-user-name">{user.name}</span>
            <Button variant="outline" className="admin-logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          <div className="admin-page-header">
            <Button className="admin-add-button" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="button-icon" />
              Back to Dashboard
            </Button>
          </div>

          <div className="admin-title-row">
            <h1 className="admin-page-title">Active Sessions ({sessions.length})</h1>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button className="admin-add-button" onClick={cleanupExpiredSessions}>
                <UserX className="button-icon" />
                Cleanup Expired
              </Button>
              <Button className="admin-add-button" onClick={fetchActiveSessions}>
                <RefreshCw className="button-icon" />
                Refresh
              </Button>
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <Card style={{ background: 'rgba(229, 0, 0, 0.1)', border: '1px solid var(--retro-red)' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Clock style={{ color: 'var(--retro-red)' }} size={20} />
                  <h3 style={{ color: 'var(--retro-red)', margin: 0 }}>Session Security Info</h3>
                </div>
                <p style={{ color: 'var(--retro-cream)', margin: 0, fontSize: '0.9rem' }}>
                  • Sessions automatically expire after <strong>2 hours of inactivity</strong><br/>
                  • Each action extends the session by another 2 hours<br/>
                  • You can force logout any user except yourself
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="admin-users-grid">
            {sessions.length === 0 ? (
              <p className="admin-empty-message">No active sessions found.</p>
            ) : (
              sessions.map((session) => (
                <Card key={session.session_id} className="admin-user-card">
                  <CardContent className="admin-user-card-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                        <img 
                          src={session.user_picture} 
                          alt={session.user_name}
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            border: '2px solid var(--retro-red)'
                          }}
                        />
                        <div>
                          <h3 className="admin-user-email">{session.user_email}</h3>
                          <p className="admin-user-name-display">{session.user_name}</p>
                          <div style={{ fontSize: '0.8rem', color: 'var(--retro-gray)' }}>
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Login:</strong> {formatActualLoginTime(session.login_time)}
                            </p>
                            <p style={{ margin: '0.25rem 0' }}>
                              <strong>Expires:</strong> {formatExpiryTime(session.expires_at)}
                            </p>
                            {session.is_current_session && (
                              <p style={{ margin: '0.25rem 0', color: 'var(--retro-red)', fontWeight: 'bold' }}>
                                (Your Current Session)
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {session.user_email.toLowerCase() !== user.email.toLowerCase() && (
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => forceLogout(session.session_id, session.user_name)}
                        >
                          <UserX className="button-icon-sm" />
                          Force Logout
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSessions;